import { createApp, type App } from 'vue';
import AntDesignVue from 'ant-design-vue';
import ResidentNotificationDrawer from './index.vue';
import type { NotificationDrawerPayload } from './types';

const MOUNT_ID = 'residentNotificationDrawerApp';
const OPEN_SELECTOR = '[data-notification-open-drawer]';

let app: App<Element> | null = null;
let instance: { open: (payload: NotificationDrawerPayload) => void } | null = null;
let currentMountEl: HTMLElement | null = null;
let triggersBound = false;

function resolveMountElement(root?: ParentNode): HTMLElement | null {
  const mountEl = root
    ? (root.querySelector<HTMLElement>(`#${MOUNT_ID}`) ?? document.getElementById(MOUNT_ID))
    : document.getElementById(MOUNT_ID);

  if (mountEl) {
    return mountEl;
  }

  const dynamicMountEl = document.createElement('div');
  dynamicMountEl.id = MOUNT_ID;
  document.body.appendChild(dynamicMountEl);

  return dynamicMountEl;
}

function mountResidentNotificationDrawer(root?: ParentNode): void {
  const mountEl = resolveMountElement(root);
  if (!mountEl) return;

  if (app && currentMountEl) {
    app.unmount();
    currentMountEl.innerHTML = '';
  }

  app = createApp(ResidentNotificationDrawer);
  app.use(AntDesignVue);
  instance = app.mount(mountEl) as unknown as {
    open: (payload: NotificationDrawerPayload) => void;
  };
  currentMountEl = mountEl;
}

function getPayloadFromElement(element: HTMLElement): NotificationDrawerPayload {
  const idValue = element.dataset.notificationId;

  return {
    id: idValue ? Number(idValue) : undefined,
    title: element.dataset.notificationTitle ?? '',
    date: element.dataset.notificationDate ?? '',
    description: element.dataset.notificationDescription ?? '',
  };
}

export function openResidentNotificationDrawer(payload: NotificationDrawerPayload): void {
  if (!app || !instance) {
    mountResidentNotificationDrawer();
  }

  instance?.open(payload);
}

function handleNotificationTrigger(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest<HTMLElement>(OPEN_SELECTOR);
  if (!trigger) return;

  event.preventDefault();
  openResidentNotificationDrawer(getPayloadFromElement(trigger));
}

export function initResidentNotificationDrawer(root?: ParentNode): void {
  mountResidentNotificationDrawer(root);

  if (triggersBound) return;
  triggersBound = true;

  document.addEventListener('click', handleNotificationTrigger);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest<HTMLElement>(OPEN_SELECTOR);
    if (!trigger) return;

    event.preventDefault();
    openResidentNotificationDrawer(getPayloadFromElement(trigger));
  });
}
